import moment from 'moment-timezone';
import {Record, List} from 'immutable';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';

import PricingGroupPrice from '../common/pricing-group-price';

const SessionType = {
    PACKAGE_SESSION: 'package',
    STANDALONE_SESSION: 'standalone',
    VOLUME_SESSION: 'volume',
    fromString: str => {
        switch (str) {
            case 'package':
                return SessionType.PACKAGE_SESSION;
            case 'standalone':
                return SessionType.STANDALONE_SESSION;
            case 'volume':
                return SessionType.VOLUME_SESSION;
            default:
                throw new Error(`Non-exhaustive switch statement: ${str}`);
        }
    },
};

const Availability = Record({
    day: '', // string, oneOf MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY
    start: null, // moment
    end: null, // moment
});

const Session = Record({
    id: '',
    sessionId: '',
    version: '',
    institutionId: '',
    title: '', // string
    volumeDiscountGroupId: null, // string
    color: null, // int
    validFrom: null, // moment
    validTo: null, // moment
    pricing: List(),
    availability: List(),
    free: null,
    fundable: true,
    flexibleHours: false,
    billOnChildSick: true,
    billOnChildAbsent: true,
    billOnClosingDays: null,
    inUse: false,
    sessionType: '',
    accountId: null,
    behaviors: new BehaviorsCollection(),
    customFte: null, // float, number or null
});

function fromAPI(data) {
    const availability = data.availability.map(
        availability =>
            new Availability({
                day: availability.day,
                start: moment(availability.start, 'HH:mm'),
                end: moment(availability.end, 'HH:mm'),
            }),
    );

    const pricing = data.pricing.map(PricingGroupPrice.fromAPI);

    return new Session({
        id: data.version,
        sessionId: data.id,
        version: data.version,
        billOnClosingDays: data.billOnClosingDays ? List(data.billOnClosingDays) : null,
        institutionId: data.institutionId,
        fundable: data.fundable,
        title: data.title,
        volumeDiscountGroupId: data.volumeDiscountGroupId,
        billOnChildSick: data.billOnChildSick,
        billOnChildAbsent: data.billOnChildAbsent,
        color: data.color,
        validFrom: data.validFrom ? moment(data.validFrom, 'YYYY-MM-DD') : null,
        validTo: data.validTo ? moment(data.validTo, 'YYYY-MM-DD') : null,
        pricing: List(pricing),
        availability: List(availability),
        free: data.free,
        flexibleHours: data.flexibleHours,
        inUse: Boolean(data.inUse),
        sessionType: SessionType.fromString(data.sessionType),
        accountId: data.accountId,
        behaviors: new BehaviorsCollection(data.behaviors),
        customFte: data.customFte,
    });
}

function toAPI(session) {
    const availability = session.availability.map(availability => ({
        day: availability.day,
        start: availability.start ? availability.start.format('HH:mm') : null,
        end: availability.end ? availability.end.format('HH:mm') : null,
    }));

    const pricing = session.pricing.map(PricingGroupPrice.fromAPI);

    return {
        id: session.sessionId,
        billOnClosingDays: session.billOnClosingDays ?? null,
        institutionId: session.institutionId,
        fundable: session.fundable,
        volumeDiscountGroupId: session.volumeDiscountGroupId,
        billOnChildSick: session.billOnChildSick,
        billOnChildAbsent: session.billOnChildAbsent,
        title: session.title,
        color: session.color,
        validFrom: session.validFrom ? session.validFrom.format('YYYY-MM-DD') : null,
        validTo: session.validTo ? session.validTo.format('YYYY-MM-DD') : null,
        pricing: pricing.toList(),
        availability: availability.toList(),
        free: session.free,
        flexibleHours: session.flexibleHours,
        sessionType: session.sessionType,
        accountId: session.accountId,
        customFte: session.customFte,
    };
}

export default {
    record: Session,
    fromAPI,
    toAPI,
    SessionType,
    Availability,
};
